import React from "react";
import styled from "styled-components";

import { reactKeyFromIDSK } from "@examspark/common";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  Loading,
  RouterLink,
  Toolbar,
} from "@examspark/common-ui";
import {
  IconAcceptLine,
  IconPlayLine,
  IconResetLine,
  IconSkipLine,
} from "@examspark/common-ui/dist/components/icons";
import { ParsedClassroom } from "@examspark/types";

import { useQueryGetCourseUnits } from "../../api/operations/course";
import { useClassroomUnitsManager } from "../../hooks/useClassroomUnitsManager";

const CourseSelectTable = styled.table`
  width: 100%;

  thead tr th {
    font-weight: normal;
    text-align: left;
    font-size: 1.5rem;
    color: #555;
  }
  td {
    border-top: solid 1px #ddd;
  }
  td,
  th {
    padding: 1rem 0.5rem;
  }
  tbody tr {
    transition: background-color 0.2s ease;
  }
  tbody tr:hover {
    background-color: #f7f7f7;
  }
`;

const ActionsTH = styled.th`
  width: 10rem;
`;
const ActionsTD = styled.td`
  text-align: right;
  display: flex;
  justify-content: flex-end;
`;

const ClassroomUnitsManager = ({
  classroom,
  ...props
}: {
  disabled?: boolean;
  classroom: ParsedClassroom;
  onUpdate?: () => void;
  onStartUnit?: (unitId: string) => void;
}) => {
  const classroomUnitsManager = useClassroomUnitsManager(classroom);

  const { data: courseUnits, isLoading } = useQueryGetCourseUnits(
    classroom?.courseId || "",
  );

  const hasClassroomUnitInProgress = classroom.units?.some(
    (u) => u.maturity === "IN_PROGRESS",
  );

  if (isLoading) return <Loading>Loading...</Loading>;

  return (
    <CourseSelectTable cellPadding={0} cellSpacing={0}>
      <thead>
        <tr>
          <th>Unit</th>
          <ActionsTH></ActionsTH>
        </tr>
      </thead>
      <tbody>
        {courseUnits?.map((unit, idx) => {
          const classroomUnit = classroom.units?.find(
            (u) => u.unit?.id === unit.id,
          );
          const unitIsConcluded = ["COMPLETE", "SKIPPED"].includes(
            classroomUnit?.maturity ?? "",
          );

          return (
            <tr key={reactKeyFromIDSK(unit)}>
              <td>
                <RouterLink
                  to={`/classroom/${classroom.id}/unit/${unit.id}`}
                  relative="path"
                >
                  Unit {idx + 1}: {unit.name}
                </RouterLink>
              </td>
              <ActionsTD>
                <Toolbar>
                  {classroomUnit?.unit?.id === unit.id ? (
                    <Button
                      stretch
                      variant={unitIsConcluded ? "plain" : "contained"}
                      disabled={
                        props.disabled ||
                        (!!classroom.unitId && classroom.unitId !== unit.id)
                      }
                      color="primary"
                      title="Finish"
                      icon={
                        unitIsConcluded ? <IconPlayLine /> : <IconAcceptLine />
                      }
                      onClick={async () => {
                        if (unitIsConcluded) {
                          await ConfirmModal.show({
                            onResolving: async (result) => {
                              if (result) {
                                await classroomUnitsManager.handleStart(
                                  unit.id,
                                );
                                props.onStartUnit?.(unit.id);
                                props.onUpdate?.();
                              }
                            },
                            children: (
                              <CautionBanner variant="plain">
                                Are you sure you want to start this unit?
                              </CautionBanner>
                            ),
                          });
                        } else {
                          await ConfirmModal.show({
                            onResolving: async (result) => {
                              if (result) {
                                await classroomUnitsManager.handleFinish(
                                  unit.id,
                                );
                                props.onUpdate?.();
                              }
                            },
                            children: (
                              <CautionBanner variant="plain">
                                Are you sure you want to mark this unit
                                complete?
                              </CautionBanner>
                            ),
                          });
                        }
                      }}
                    >
                      {unitIsConcluded ? "Resume" : "Finish"}
                    </Button>
                  ) : (
                    <Button
                      title="Begin"
                      icon={<IconPlayLine />}
                      disabled={props.disabled || hasClassroomUnitInProgress}
                      onClick={async () => {
                        await ConfirmModal.show({
                          onResolving: async (result) => {
                            if (result) {
                              await classroomUnitsManager.handleStart(unit.id);
                              props.onStartUnit?.(unit.id);
                              props.onUpdate?.();
                            }
                          },
                          children: (
                            <CautionBanner variant="plain">
                              Are you sure you want to start this unit?
                            </CautionBanner>
                          ),
                        });
                      }}
                    >
                      Start
                    </Button>
                  )}

                  <Button
                    title="Skip"
                    icon={<IconSkipLine />}
                    disabled={props.disabled || unitIsConcluded}
                    onClick={async () => {
                      await ConfirmModal.show({
                        onResolving: async (result) => {
                          if (result) {
                            await classroomUnitsManager.handleSkip(unit.id);
                          }
                        },
                        children: (
                          <CautionBanner variant="plain">
                            Are you sure you want to skip this unit?
                          </CautionBanner>
                        ),
                      });
                    }}
                  >
                    Skip
                  </Button>

                  <Button
                    title="Reset"
                    disabled={props.disabled}
                    icon={<IconResetLine />}
                    onClick={async () => {
                      await ConfirmModal.show({
                        onResolving: async (result) => {
                          if (result) {
                            await classroomUnitsManager.handleReset(unit.id);
                          }
                        },
                        children: (
                          <CautionBanner variant="plain">
                            Are you sure you want to reset this unit?
                          </CautionBanner>
                        ),
                      });
                    }}
                  >
                    Reset
                  </Button>
                </Toolbar>
              </ActionsTD>
            </tr>
          );
        })}
      </tbody>
    </CourseSelectTable>
  );
};

export default ClassroomUnitsManager;
